import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { patientFormApi } from '@/services/patientFormApi';
import PatientFormRenderer from './PatientFormRenderer';
import type { PatientFormTemplate } from '@/types/patientForm';

export default function PatientFormTemplateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<PatientFormTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'preview' | 'config'>('preview');

  useEffect(() => {
    if (id) {
      loadTemplate();
    }
  }, [id]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await patientFormApi.getTemplate(Number(id));
      setTemplate(data);
    } catch (err: any) {
      console.error('Error loading template:', err);
      setError(err.response?.data?.message || 'Failed to load template');
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleTestSubmit = async (formData: Record<string, any>) => {
    console.log('Form data:', formData);
    toast.success('Form validation passed! (Test mode - data not saved)');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error || 'Template not found'}</p>
        <Link
          to="/pathology/patients/form-builder"
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Back to templates
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Link
              to="/pathology/patients/form-builder"
              className="text-blue-600 hover:text-blue-800"
            >
              ← Back
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">{template.name}</h1>
          <p className="text-sm text-gray-600 mt-1">{template.description}</p>
          <div className="flex items-center gap-3 mt-2">
            {template.is_default && (
              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                Default
              </span>
            )}
            {!template.is_active && (
              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                Inactive
              </span>
            )}
            <span className="text-sm text-gray-500">Version {template.version}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('preview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'preview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Preview
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'config'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Configuration
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'preview' ? (
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <strong>Test Mode:</strong> This is a preview. Form data will not be saved.
              </p>
            </div>
            <PatientFormRenderer
              config={template.form_config}
              onSubmit={handleTestSubmit}
              onCancel={() => navigate('/pathology/patients/form-builder')}
            />
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Template Configuration</h3>
          <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-xs">
            {JSON.stringify(template.form_config, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
